import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    Typography,
    MenuItem,
    CircularProgress,
    Chip,
    Box,
    TextField,
} from '@mui/material';
import { apiService } from '../../services/api';
import { getEnvironmentConfig } from '../../utils';
import { useNavigate, useParams } from 'react-router-dom';
import {
    FormContainer,
    FormTitle,
    FormSection,
    FormRow,
    ButtonContainer,
    SubmitButton,
    ResetButton,
} from './BankInfoForm.styles';
import { useToast } from '../../providers/ToastContext';
import { useForm, Controller } from 'react-hook-form';

interface Country {
    id: number;
    name: string;
    status: string;
    countryCode: string;
}

interface BankInfoFormProps {
    onSuccess?: () => void;
    onCancel?: () => void;
}

type FormData = {
    name: string;
    email: string;
    phone: string;
    city: string;
    state: string;
    zipcode: string | number;
    accountNumber: string | number;
    accountType: string;
    routingNumber: string | number;
    transmissionType: string;
    merchantId: string;
    wires: string | number;
    countryCode: string;
    ibanNumber: string | number;
    swiftCode: string;
};

const BankInfoForm: React.FC<BankInfoFormProps> = ({ onSuccess, onCancel }) => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { id } = useParams<{ id: string }>();
    const config = getEnvironmentConfig();
    const endpoint = `${config.API_BASE_URL}/bankinfo/create`;

    const [countryCodes, setCountryCodes] = useState<Country[]>([]);
    const [loading, setLoading] = useState(false);

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<FormData>({
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            city: '',
            state: '',
            zipcode: '',
            accountNumber: '',
            accountType: 'Business Savings',
            routingNumber: '',
            transmissionType: 'same day',
            merchantId: id || '',
            wires: '',
            countryCode: '',
            ibanNumber: '',
            swiftCode: '',
        },
    });

    const countryCodeValue = watch('countryCode');

    useEffect(() => {
        const fetchCountryCodes = async () => {
            try {
                setLoading(true);
                const response = await apiService.getCountryCodes();
                if (response.success && Array.isArray(response.data)) {
                    setCountryCodes(response.data);
                }
            } catch (err) {
                showToast('Error fetching country codes', 'error');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchCountryCodes();
    }, []);

    const onSubmit = async (data: FormData) => {
        setLoading(true);
        try {
            const payload = {
                ...data,
                zipcode: Number(data.zipcode),
                accountNumber: Number(data.accountNumber),
                routingNumber: Number(data.routingNumber),
                merchantId: Number(data.merchantId),
                wires: Number(data.wires),
                ibanNumber: data.countryCode === '+1' ? null : data.ibanNumber,
                swiftCode: data.countryCode === '+1' ? null : data.swiftCode,
            };

            const response = await apiService.createBankInfo(payload);
            if (response.success) {
                showToast('Bank info created successfully!', 'success');
                reset();
                onSuccess?.();
                setTimeout(() => navigate('/bankinfo/create'), 2000);
            } else {
                showToast('Error occurred while creating bank info', 'error');
            }
        } catch (err) {
            showToast(
                'An unexpected error occurred. Please try again.',
                'error'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <FormContainer elevation={3}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <FormTitle>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Create Bank Info
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            justifyContent: 'center',
                            mt: 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            API Endpoint:
                        </Typography>
                        <Chip
                            label={`POST ${endpoint}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    </Box>
                </FormTitle>

                <FormSection>
                    {/* Name & Email */}
                    <FormRow>
                        <Controller
                            name="name"
                            control={control}
                            rules={{ required: 'Name is required' }}
                            render={({ field }) => (
                                <TextField
                                    label="Name"
                                    fullWidth
                                    {...field}
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                />
                            )}
                        />
                        <Controller
                            name="email"
                            control={control}
                            rules={{ required: 'Email is required' }}
                            render={({ field }) => (
                                <TextField
                                    label="Email"
                                    fullWidth
                                    {...field}
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                />
                            )}
                        />
                    </FormRow>

                    {/* Country Code & Phone */}
                    <FormRow>
                        <Controller
                            name="countryCode"
                            control={control}
                            rules={{ required: 'Country Code is required' }}
                            render={({ field }) => (
                                <TextField
                                    select
                                    label="Country Code"
                                    fullWidth
                                    {...field}
                                    error={!!errors.countryCode}
                                    helperText={errors.countryCode?.message}
                                >
                                    {loading ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} />
                                        </MenuItem>
                                    ) : (
                                        countryCodes.map(country => (
                                            <MenuItem
                                                key={country.id}
                                                value={country.countryCode}
                                            >
                                                {country.countryCode} (
                                                {country.name})
                                            </MenuItem>
                                        ))
                                    )}
                                </TextField>
                            )}
                        />
                        <Controller
                            name="phone"
                            control={control}
                            rules={{ required: 'Phone is required' }}
                            render={({ field }) => (
                                <TextField
                                    label="Phone"
                                    fullWidth
                                    {...field}
                                    error={!!errors.phone}
                                    helperText={errors.phone?.message}
                                />
                            )}
                        />
                    </FormRow>

                    {/* Conditional IBAN & Swift */}
                    {countryCodeValue !== '+1' && (
                        <FormRow>
                            <Controller
                                name="ibanNumber"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        label="IBAN Number"
                                        type="number"
                                        fullWidth
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                name="swiftCode"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        label="Swift Code"
                                        fullWidth
                                        {...field}
                                    />
                                )}
                            />
                        </FormRow>
                    )}

                    {/* City & State */}
                    <FormRow>
                        <Controller
                            name="city"
                            control={control}
                            rules={{ required: 'City is required' }}
                            render={({ field }) => (
                                <TextField
                                    label="City"
                                    fullWidth
                                    {...field}
                                    error={!!errors.city}
                                    helperText={errors.city?.message}
                                />
                            )}
                        />
                        <Controller
                            name="state"
                            control={control}
                            rules={{ required: 'State is required' }}
                            render={({ field }) => (
                                <TextField
                                    label="State"
                                    fullWidth
                                    {...field}
                                    error={!!errors.state}
                                    helperText={errors.state?.message}
                                />
                            )}
                        />
                    </FormRow>

                    {/* Zipcode */}
                    <FormRow>
                        <Controller
                            name="zipcode"
                            control={control}
                            rules={{ required: 'Zipcode is required' }}
                            render={({ field }) => (
                                <TextField
                                    label="Zipcode"
                                    type="number"
                                    fullWidth
                                    {...field}
                                    error={!!errors.zipcode}
                                    helperText={errors.zipcode?.message}
                                />
                            )}
                        />
                    </FormRow>

                    {/* Account Number & Account Type */}
                    <FormRow>
                        <Controller
                            name="accountNumber"
                            control={control}
                            rules={{ required: 'Account Number is required' }}
                            render={({ field }) => (
                                <TextField
                                    label="Account Number"
                                    type="number"
                                    fullWidth
                                    {...field}
                                    error={!!errors.accountNumber}
                                    helperText={errors.accountNumber?.message}
                                />
                            )}
                        />
                        <Controller
                            name="accountType"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    select
                                    label="Account Type"
                                    fullWidth
                                    {...field}
                                >
                                    <MenuItem value="Business Savings">
                                        Business Savings
                                    </MenuItem>
                                    <MenuItem value="Checking">
                                        Checking
                                    </MenuItem>
                                    <MenuItem value="Corporate Account">
                                        Corporate Account
                                    </MenuItem>
                                </TextField>
                            )}
                        />
                    </FormRow>

                    {/* Routing Number & Transmission Type */}
                    <FormRow>
                        <Controller
                            name="routingNumber"
                            control={control}
                            rules={{ required: 'Routing Number is required' }}
                            render={({ field }) => (
                                <TextField
                                    label="Routing Number"
                                    type="number"
                                    fullWidth
                                    {...field}
                                    error={!!errors.routingNumber}
                                    helperText={errors.routingNumber?.message}
                                />
                            )}
                        />
                        <Controller
                            name="transmissionType"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    select
                                    label="Transmission Type"
                                    fullWidth
                                    {...field}
                                >
                                    <MenuItem value="same day">
                                        Same Day
                                    </MenuItem>
                                    <MenuItem value="next day">
                                        Next Day
                                    </MenuItem>
                                    <MenuItem value="standard">
                                        Standard
                                    </MenuItem>
                                </TextField>
                            )}
                        />
                    </FormRow>

                    {/* Merchant ID & Wires */}
                    <FormRow>
                        <Controller
                            name="merchantId"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    label="Merchant ID"
                                    type="number"
                                    fullWidth
                                    {...field}
                                    InputProps={{ readOnly: true }}
                                />
                            )}
                        />
                        <Controller
                            name="wires"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    label="Wires"
                                    type="number"
                                    fullWidth
                                    {...field}
                                />
                            )}
                        />
                    </FormRow>
                </FormSection>

                <ButtonContainer>
                    <ResetButton
                        variant="outlined"
                        onClick={() => reset()}
                        disabled={isSubmitting || loading}
                    >
                        Reset
                    </ResetButton>
                    <ResetButton
                        variant="outlined"
                        onClick={() => navigate('/bankinfo/create')}
                        disabled={isSubmitting || loading}
                    >
                        Cancel
                    </ResetButton>
                    <SubmitButton
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || loading}
                    >
                        {isSubmitting || loading ? (
                            <CircularProgress size={24} />
                        ) : (
                            'Create Bank Info'
                        )}
                    </SubmitButton>
                </ButtonContainer>
            </form>
        </FormContainer>
    );
};

export default BankInfoForm;
